import React, { useState, useEffect, useContext } from 'react';
import { isAuthorised, Spacer, useModal } from '@adserve/adserve-react-components';
import { addInvoicePanelStyle, billingSummaryPanelStyle, panelTitleStyle } from './BespokeBillingStyles';
import CampaignBreakdown from './CampaignBreakdown';
import InvoicingBreakdown from './InvoicingBreakdown';
import BespokeInvoiceList from './BespokeInvoiceList';
import BespokeInvoiceManager from './BespokeInvoiceManager';
import {
    GetOffairTypes,
    GetVatRates,
    GetVatDates,
    UpdateBillingPlanInvoice,
    GetBillingPlanInvoicesByCampaign,
    DeleteBillingPlanInvoice,
    GetAllEffectiveVats,
    GetOffairActivityHistoriesForBilling
} from '../../../services/CampaignsDataService';
import { pageContainerStyle } from '../CampaignStyles';
import { RemoveById, ReplaceOrPushById } from '../../../services/ArrayService';
import { AirtimePlanningContext } from '../../../contexts/AirtimePlanningContext';
import { BusinessTypes } from '../../../constants/Enums';
import moment from 'moment';

const BespokeBilling = ({
    campaign,
    changeMade,
    readOnly,
    currentDeal,
    selectedGoal,
    currentCampaignVersionId = window.emptyGuid
}) => {
    const disabled = readOnly || !isAuthorised('C_BILL_PLAN');
    const { addMessage } = useModal();
    const { propertyStats } = useContext(AirtimePlanningContext);

    const [billingPlanInvoices, setBillingPlanInvoices] = useState([]);
    const lastDayOfCurrentMonth = moment().utc(true).endOf('month').startOf('day');

    const [editingInvoice, setEditingInvoice] = useState({ invoiceDate: lastDayOfCurrentMonth });
    const [offairTypes, setOffairTypes] = useState([]);
    const [vatDates, setVatDates] = useState([]);
    const [vatRates, setVatRates] = useState([]);
    const [isSAndP, setIsSAndP] = useState(
        campaign?.businessType?.toLowerCase() === BusinessTypes.SANDP.name.toLocaleLowerCase()
    );
    const [offairActivitiesForBilling, setOffairActivitiesForBilling] = useState([]);
    const [sandPBudgetDetails, setSandPBudgetDetails] = useState({});

    const whiteButtonStyle = {
        border: '2px solid #CCCCCC',
        backgroundColor: '#FFFFFF',
        cursor: 'pointer'
    };

    const getInvoices = async () => {
        if (!campaign?.id) {
            return;
        }
        const data = await GetBillingPlanInvoicesByCampaign(campaign?.id);
        setBillingPlanInvoices(data);
        setEditingInvoice({});
    };

    const getOffairTypes = async () => {
        if (!campaign?.id) {
            return;
        }
        const data = await GetOffairTypes();
        setOffairTypes(data);
    };

    const getVatDates = async () => {
        if (!campaign?.id) {
            return;
        }
        const data = await GetAllEffectiveVats();
        setVatDates(data);
    };

    const getVatRates = async () => {
        if (!campaign?.id) {
            return;
        }
        const data = await GetVatRates();
        setVatRates(data);
    };
    const getOffairActivitiesForBilling = async () => {
        if (!campaign?.id) {
            return;
        }
        GetOffairActivityHistoriesForBilling(campaign.id)
            .then((data) => {
                setOffairActivitiesForBilling(data.data.offairActivitiesForBilling);
                setSandPBudgetDetails(data.data);
            })
            // eslint-disable-next-line no-console
            .catch(console.log);
    };

    useEffect(() => {
        getInvoices();
        getOffairTypes();
        getVatDates();
        getVatRates();

        const localCampaign = campaign;
        if (
            localCampaign.campaignStatusName === 'Booked' ||
            localCampaign.campaignStatusName === 'Running' ||
            localCampaign.campaignStatusName === 'Completed'
        ) {
            setOffairActivitiesForBilling(campaign?.offairActivities);
        } else {
            if (localCampaign.versionNumber >= 1) {
                getOffairActivitiesForBilling();
            }
        }
    }, [campaign.id, currentCampaignVersionId]);

    const onEditInvoice = (invoice) => {
        setEditingInvoice({ ...invoice });
    };

    const onDeleteInvoiceLine = (il) => {
        const mutar = RemoveById(editingInvoice?.billingPlanInvoiceLines ?? [], il.id);
        setEditingInvoice({ ...editingInvoice, billingPlanInvoiceLines: mutar });
    };

    const onDeleteInvoice = (invoice) => {
        addMessage({
            message: window.translate('Are you sure you want to delete this invoice?'),
            buttons: [
                {
                    text: window.translate('No'),
                    style: { ...whiteButtonStyle, width: 87 },
                    callback: null
                },
                {
                    text: window.translate('Yes'),
                    style: { cursor: 'pointer' },
                    callback: () => deleteInvoice(invoice)
                }
            ]
        });
    };

    const deleteInvoice = async (invoice) => {
        try {
            await DeleteBillingPlanInvoice(invoice.id);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(`Failed to delete invoice ${invoice.id}\n${err.message}`);
            addMessage({
                message: 'An error has occured while attempting to delete this invoice.',
                buttons: [{ text: window.translate('OK') }]
            });
        }
        await getInvoices();
    };

    const onApplyClick = async (invoice) => {
        if (disabled) {
            return;
        }

        invoice.billingPlanInvoiceLines.map((l) => {
            l.invoiceLineDate = invoice.invoiceDate;
        });
        try {
            await UpdateBillingPlanInvoice({ ...invoice, campaignId: campaign.id });
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(`Failed to update invoice ${invoice.id}\n${err.message}`, invoice);
            addMessage({
                message: 'An error has occured while attempting to update this invoice.',
                buttons: [{ text: window.translate('OK') }]
            });
        }
        await getInvoices();
    };

    const onAddClick = (invoiceLine, invoiceDate) => {
        if (disabled) {
            return;
        }
        const mutar = ReplaceOrPushById(editingInvoice?.billingPlanInvoiceLines ?? [], invoiceLine);
        setEditingInvoice({ ...editingInvoice, billingPlanInvoiceLines: mutar, invoiceDate: invoiceDate });
    };

    const onInvoiceDateChange = (event) => {
        setEditingInvoice({ ...editingInvoice, invoiceDate: event.target.value });
    };

    const onPoNumberChange = (event) => {
        setEditingInvoice({ ...editingInvoice, poNumber: event.target.value });
    };

    // ASSP-6309 Use invoice name for optional narrative
    const onNarrativeChange = (event) => {
        setEditingInvoice({ ...editingInvoice, name: event.target.value });
    };

    const getDefaultVatRate = () => {
        const retVal = vatRates.find((vr) => vr.isDefault);
        return retVal;
    };

    const getZeroVatRate = () => {
        const retVal = vatRates.find((vr) => vr.vatCode === 'ZERO');
        return retVal;
    };

    const getDefaultVatDate = () => {
        const vatRate = getDefaultVatRate();
        if (!vatDates?.length || !vatRate) {
            return;
        }
        const retVal = vatDates.find((vd) => vd.vatId === vatRate.id);
        return retVal;
    };

    const calcVatRate = (vatDateId) => {
        if (!vatDates?.length) {
            return 1;
        }
        const vatDate = vatDates.find((vd) => vd.id === vatDateId);
        if (!vatDate) {
            return 1;
        }
        const rate = vatDate.rate ? 1 + vatDate.rate / 100 : 1;
        return rate;
    };

    const getVatRateById = (vatRateId) => {
        if (!vatDates?.length) {
            return 0;
        }
        const vatDate = vatDates.find((vd) => vd.vatId === vatRateId);
        if (!vatDate) {
            return 0;
        }
        return vatDate.rate;
    };

    const calcOffairTotals = () => {
        let invoiceTotal = 0;
        let commissionTotal = 0;
        if (offairActivitiesForBilling?.length > 0) {
            offairActivitiesForBilling.forEach((oa) => {
                if (oa.status === 3) {
                    const amount = parseFloat(oa.price);
                    let commission = 0;
                    if (oa.commission > 0) {
                        commission = (amount * parseFloat(oa.commission)) / 100;
                    }
                    invoiceTotal += amount;
                    commissionTotal += commission;
                }
            });
        }
        return {
            invoiceTotal,
            commissionTotal
        };
    };

    const calcTotals = () => {
        let invoiceTotal = 0;
        let vatTotal = 0;
        let commissionTotal = 0;
        billingPlanInvoices?.forEach((bpi) => {
            bpi?.billingPlanInvoiceLines?.forEach((il) => {
                const amount = parseFloat(il.airtimeAmount) + parseFloat(il.offAirAmount);
                const vat = amount * calcVatRate(il.vatDateId) - amount;
                const commission = (amount * calcVatRate(il.vatDateId) * il.commissionRate) / 100;
                invoiceTotal += amount;
                vatTotal += vat;
                commissionTotal += commission;
            });
        });
        return {
            invoiceTotal,
            vatTotal,
            commissionTotal
        };
    };

    const totals = calcTotals();
    const offairTotal = calcOffairTotals();

    const sumAllPropertiesLines = (field) => {
        let total = 0;
        if (propertyStats.length) {
            propertyStats?.forEach((b) => {
                if (b !== undefined) {
                    total = total + (b[field] !== undefined ? parseFloat(b[field] ?? 0) : 0);
                }
            });
        }
        return total?.toFixed(2);
    };

    console.log('BespokeBilling invoices', billingPlanInvoices);

    return (
        <div style={pageContainerStyle}>
            <div style={billingSummaryPanelStyle}>
                <div style={{ margin: '20px 29px 0px 22px' }}>
                    <span style={panelTitleStyle}>{window.translate('Bespoke Billing Summary')}</span>
                </div>
                <div style={{ display: 'flex', marginLeft: 22, marginBottom: 25 }}>
                    <div>
                        <BespokeInvoiceList
                            invoices={billingPlanInvoices}
                            calcVatRate={getVatRateById}
                            onEditInvoice={onEditInvoice}
                            onDeleteInvoice={onDeleteInvoice}
                            disabled={disabled}
                            isSAndP={isSAndP}
                        />
                    </div>
                    <div>
                        <CampaignBreakdown
                            campaign={campaign}
                            calcVatRate={getVatRateById}
                            invoices={billingPlanInvoices}
                            sumAllPropertiesLines={sumAllPropertiesLines}
                            isSAndP={isSAndP}
                            offairTotal={offairTotal}
                            currentDeal={currentDeal}
                            selectedGoal={selectedGoal}
                            offairActivitiesForBilling={offairActivitiesForBilling}
                            sandPBudgetDetails={sandPBudgetDetails}
                        />
                    </div>
                    <div>
                        <InvoicingBreakdown
                            campaign={campaign}
                            invoices={billingPlanInvoices}
                            invoiceTotal={totals.invoiceTotal}
                            sumAllPropertiesLines={sumAllPropertiesLines}
                            isSAndP={isSAndP}
                            offairTotal={offairTotal}
                            calcVatRate={getVatRateById}
                            currentDeal={currentDeal}
                            selectedGoal={selectedGoal}
                            sandPBudgetDetails={sandPBudgetDetails}
                        />
                    </div>
                </div>
            </div>

            <Spacer height={16} width={0} />

            <div style={addInvoicePanelStyle}>
                <div style={{ marginTop: 13, marginLeft: 21 }}>
                    <span style={panelTitleStyle}>{window.translate('Create New Invoice')}</span>
                </div>
                <div style={{ marginLeft: 21, marginTop: -17 }}>
                    <BespokeInvoiceManager
                        campaign={campaign}
                        readOnly={disabled}
                        invoice={editingInvoice}
                        invoices={billingPlanInvoices}
                        offairTypes={offairTypes}
                        vatRates={vatRates}
                        vatDates={vatDates}
                        changeMade={changeMade}
                        onApplyClick={onApplyClick}
                        onAddClick={onAddClick}
                        onInvoiceDateChange={onInvoiceDateChange}
                        onNarrativeChange={onNarrativeChange}
                        onPoNumberChange={onPoNumberChange}
                        calcVatRate={getVatRateById}
                        defaultVatRate={getDefaultVatRate()}
                        defaultVatDate={getDefaultVatDate()}
                        zeroVatRate={getZeroVatRate()}
                        onDeleteInvoiceLine={onDeleteInvoiceLine}
                    />
                </div>
            </div>

            <Spacer height={25.5} width={0} />
        </div>
    );
};

export default BespokeBilling;
